import { Show as Avatar } from "components/Avatar";
import { Collection as Buildings } from "components/Buildings";
import { CardDynamic as Documents } from "components/Documents";
import Projects from "components/Projects/CollectionDynamic";
import { Dashboard } from "components/Properties";
import { Breadcrumbs, Header, LoadingState, Workspace } from "components/Shared";
import { Collection as Tenancies } from "components/Tenancies";
import { PropertyCollection as Tenants } from "components/Tenants";
// The property's Leases tab lists LEASE AGREEMENTS — the Yardi-side record —
// not TCP's own `Lease` rows. components/Leases/PropertyCollection read
// `property.leases`, which /lease_data stopped writing with the amendment ERD,
// so it showed "No Leases found" on every Yardi-fed property.
import { PropertyTab as Leases } from "v2/components/LeaseAgreements";
import { Collection as Users } from "components/Users";
import { PropertyFormsTab } from "v2/components/Forms";
import { ResourceContext, UserContext } from "context";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import { isFeatureToggled } from "lib";
import moment from "moment";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router";
import { withRouter } from "react-router-dom";
import TodoTab from "../Shared/TodoTab";

const Property = (props: RouteComponentProps) => {
	const { match, history } = props;
	const { currentUser } = useContext(UserContext) as { currentUser: tcpinpoint.User };
	const id = _.get(match, "params.id");
	const [property, setProperty] = useState({} as any);
	const [, renderTrigger] = useState(moment());
	const [datasource] = useState(new Datasource({ mainModelName: "property", renderTrigger: renderTrigger }));
	const tabQueryStringParameters = useMemo(() => ({ property_id: id }), [id]);
	// PROPERTY ADMINISTRATOR — membership of `property.users`, the very set
	// PropertyPolicy#administrator? checks server-side. Since 31 Aug 2026 the
	// property-wide tenancy and tenant lists are authorized, so asking for them as
	// a non-administrator earns a 403; the tabs that now own those requests are
	// handed this and do not ask. `property` starts as `{}` and this flips to true
	// when it loads.
	const isPropertyAdministrator = _.includes(_.map(_.get(property, "users", []), "id"), _.get(currentUser, "id"));

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) => {
			if (response.success) setProperty(response.normalizedMainModelResponse);
		});
		return () => sub.unsubscribe();
	}, [datasource, setProperty]);
	
	// API call
	useEffect(() => {
		if (!_.isNil(id))
			datasource.get("v2", `properties/${id}`, {
				queryStringParameters: {
					fields: JSON.stringify({
						addresses: ["street_1", "street_2", "locality_name", "region_iso_alpha"],
						buildings: ["name", "area", "precinct", "shape", "tenancies"],
						users: ["firstname", "surname", "name", "email", "phone_number"],
					})
				}
			});
	}, [id, datasource]);

	// THE TENANCY AND TENANT FETCHES THAT USED TO LIVE HERE ARE GONE (Sim,
	// 1 Sep 2026). Both asked for the property's WHOLE book —
	// `GET /v2/tenancies/index_with_joins?property_id=&per_page=-1` and
	// `GET /v2/tenants?property_id=&per_page=-1` — on every property page view,
	// whether or not either tab was opened, and hung the arrays off
	// `property.tenancies` / `property.tenants` for the two tabs to sort and
	// filter in the browser. On a mall that is thousands of rows in one response,
	// and TenancySerializer folds the amendment tree per row
	// (V2::TenanciesController::AMENDMENT_PRELOADS loads every lease agreement
	// and amendment behind it), so the API ran out of memory. Both tabs now own
	// their own request and ask for one page at a time, like the Projects and
	// Leases tabs beside them.
	//
	// The `GET /v2/leases?property_id=&per_page=-1` that used to live here is
	// GONE with the old Leases tab. It existed only to fill `property.leases` for
	// components/Leases/PropertyCollection; the tab that replaced it
	// (v2/components/LeaseAgreements/PropertyTab) fetches its own rows, paged, so
	// this was an unbounded request on every property page view feeding nothing.
	//
	// `GET /v2/projects?property=&per_page=-1` went the same way on 27 Aug 2026.
	// Its only consumer was the `projectIds` allow-list the Tenancies tab checked
	// a unit's project against — which withheld the link from every PENDING
	// project, because that index applies `.finalized` (D3). The Projects tab
	// fetches its own rows from `tabQueryStringParameters`.

	if (!_.isEmpty(property)) {
		const { id, name, avatar, addresses } = property;
		// The same test as `isPropertyAdministrator` above — aliased rather than
		// recomputed so the two can never drift apart.
		const isAdministrator = isPropertyAdministrator;
		return (
			<ResourceContext.Provider value={property}>
				<Breadcrumbs
					key="property-breadcrumbs"
					items={[{ link: "/properties", title: "properties" }, { title: name }]}
				/>
				<Header
					resource={property}
					stamp={<Avatar url={avatar} name={name} />}
					title={name}
					subtitle={!!addresses && _.compact(_.map(addresses, a => _.get(a, "locality.name"))).join("")}
					toolbar={[
						isAdministrator && {
							onClick: () => history.push(`/projects/new?property_id=${id}`),
							label: "start_new_project",
							icon: "new"
						},
						// Property administrators (members of property.users) and company
						// admins. PropertySettingsPolicy enforces the same pair server-side.
						(isAdministrator || !!(currentUser as any).app_admin) && {
							onClick: () => history.push(`/properties/${id}/edit`),
							label: "edit"
						}
					]}
				/>
				<Workspace
					id={id}
					className={["show", "property"]}
					key="property-main"
					tabs={[
						<Projects
							name="projects"
							key="property-projects"
							queryStringParameters={tabQueryStringParameters}
						/>,
						<Buildings
							name="buildings"
							key="property-buildings"
							property={property}
							buildings={property.buildings as tcpinpoint.Building[]}
						/>,
						// Every feature-flagged tab below carries `icon="protected"`, which puts
						// the padlock in the tab label — Shared/Tabs.tsx renders it from the
						// tab element's own props. It marks the tab as gated: here by a
						// feature flag rather than by administrator rights, but the same
						// signal the project workspace's Lease tab gives.
						//
						// Plain `//`, NOT `{/* */}`: this is a JS array literal, so the JSX
						// form would be an empty object element and render a stray tab.
						!!isFeatureToggled("tenancies_tab", currentUser) && (<Tenancies name="tenancies" key="property-tenancies" property={property} isAdministrator={isAdministrator} icon="protected" />),
						!!isFeatureToggled("tenants_tab", currentUser) && (<Tenants name="tenants" key="property-tenants" property={property} isAdministrator={isAdministrator} icon="protected" />),
						// Gated by `lease-agreements`, the same flag as the top-level Leases
						// section and the amendment endpoints — one switch turns the whole
						// feature on for a user. The old `leases_tab` flag is retired and
						// gone from User::FEATURE_FLAGS.
						// The TAB IS ALWAYS RENDERED for a flagged user, administrator or not
						// (Sim, 31 Aug 2026): `isAdministrator` is passed DOWN rather than used
						// to hide it, so someone who cannot see this property's leases is told
						// why instead of finding a tab that is simply absent. PropertyPolicy
						// #lease_agreements? is the same rule server-side, and PropertyTab does
						// not fetch when the answer is already known.
						!!isFeatureToggled("lease-agreements", currentUser) && (<Leases name="leases" key="property-leases" property={property} isAdministrator={isAdministrator} icon="protected" />),
						<Documents
							name="documents"
							key="property-documents"
							resource={property}
							viewBy="grid"
							upload={isAdministrator}
							queryStringParameters={tabQueryStringParameters}
							visibleTo={`All users on projects at ${property.name} can view and download these documents`}
						/>,
						// FLAG ONLY, no `isAdministrator` (Sim, 22 Sep 2026) — the same
						// shape as Tenancies, Tenants and Leases above. A flagged
						// non-administrator gets the padlocked tab and PropertyFormsTab's
						// own "not property admin" message, rather than a tab that is
						// simply not there.
						!!isFeatureToggled("property-form", currentUser) && (
							<PropertyFormsTab
								name="forms"
								key="property-forms"
								property={property}
								isAdministrator={isAdministrator}
								icon="protected"
							/>
						),
						<Users name="parties" key="property-team" users={property.users} isAdministrator={isAdministrator} />,
						<Dashboard name="dashboard" key="property-dashboard" property={property} />,
						<TodoTab name="ToDo" key="todo-tab" propertyId={id} users={property.users} todoKind="property" />,
					]}
				/>
			</ResourceContext.Provider>
		);
	}
	return <LoadingState />;
};

export default withRouter(Property);
